import {
  FormLabel,
  FormControl,
  FormErrorMessage,
  useTheme,
} from '@chakra-ui/react';
import { Control, Controller, FieldError } from 'react-hook-form';
import { OptionTypeBase, StylesConfig } from 'react-select';
import ReactAsyncSelect, { AsyncProps } from 'react-select/async';

export type AsyncSelectOption = {
  label: string;
  value: string | null;
};

interface ISelectError extends FieldError {
  value: {
    message: string;
  };
}

interface ISelectProps extends AsyncProps<OptionTypeBase> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  name: string;
  label?: string;
  error?: ISelectError;
  loadOptions: (inputValue: string) => void;
  defaultValue?: OptionTypeBase;
  isDisabled?: boolean;
}

export const AsyncSelect = ({
  name,
  control,
  loadOptions,
  label,
  error,
  isDisabled = false,
  ...rest
}: ISelectProps): JSX.Element => {
  const theme = useTheme();
  const menuPortalTarget =
    typeof document !== 'undefined' ? document.body : undefined;

  const customStyles: StylesConfig<OptionTypeBase, false> = {
    menu: (provided) => ({
      ...provided,
      borderRadius: theme.radii.md,
      padding: 15,
      zIndex: 4,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 240,
      overflowY: 'auto',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 1500,
    }),
    control: (provided, state) => {
      let shadow = provided.boxShadow;

      if (state.isFocused || state.menuIsOpen) {
        shadow = `0 0 0 1px ${theme.colors.blue[300]}`;
      } else if (error) {
        shadow = `0 0 0 1px ${theme.colors.red[500]}`;
      }

      let border = theme.colors.gray[300];

      if (state.isFocused || state.menuIsOpen) {
        border = `${theme.colors.blue[300]}`;
      } else if (error) {
        border = `${theme.colors.red[500]}`;
      }

      let opacity = 1;
      let cursor = 'auto';

      if (state.isDisabled) {
        opacity = 0.4;
        cursor = 'not-allowed';
      }

      return {
        ...provided,
        backgroundColor: theme.colors.gray[100],
        borderRadius: theme.radii.md,
        borderColor: border,
        boxShadow: shadow,
        cursor,
        fontSize: '18px',
        height: 48,
        opacity,
        padding: '0 4px',
        ':hover': {
          borderColor: border,
        },
      };
    },
    dropdownIndicator: (provided) => ({
      ...provided,
      color: theme.colors.gray[700],
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
    option: (provided, state: { isSelected: boolean }) => ({
      ...provided,
      borderRadius: theme.radii.md,
      color: state.isSelected ? 'white' : theme.colors.gray[700],
      padding: '10px 20px',
      marginBottom: 4,
      cursor: 'pointer',
      height: 44,
    }),
  };

  return (
    <FormControl isInvalid={!!error} isDisabled={isDisabled}>
      {!!label && <FormLabel htmlFor={name}>{label}</FormLabel>}

      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <ReactAsyncSelect
            cacheOptions
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            loadOptions={loadOptions}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            styles={customStyles}
            placeholder=""
            loadingMessage={() => 'Carregando...'}
            noOptionsMessage={({ inputValue }) =>
              !inputValue ? 'Começe a digitar...' : 'Nenhum encontrado'
            }
            isDisabled={isDisabled}
            menuPortalTarget={menuPortalTarget}
            menuPosition="fixed"
            {...rest}
          />
        )}
      />

      {!!error && (
        <FormErrorMessage position="absolute">
          {error.message || error.value.message}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};
